<template>
  <FormBuilder
    class="space-y-4"
    :validation-schema="validationSchema"
    :initial-values="initialValues"
    @submit="onSubmit"
    @input="error = null"
    v-slot="{ isSubmitting }"
  >
    <InputText id="name" name="name" :label="$t('intentName')" required />
    <TextareaField
      id="description"
      name="description"
      :label="$t('description')"
      required
    />
    <TextareaField
      id="context"
      name="context"
      rows="4"
      maxLength="2000"
      :label="$t('context')"
      required
    />
    <div class="flex gap-x-2 [&>*]:grow">
      <SingleSelect
        id="type"
        name="type"
        :label="$t('type')"
        :options="[
          { value: 'dynamic', label: $t('intents_l.create.types.dynamic') },
          { value: 'catalogue', label: $t('intents_l.create.types.catalogue') }
        ]"
        @change="(e) => intentType = e.target.value"
        required
      />
    </div>
    <div v-if="intentType === 'dynamic'">
      <JsonEditorVue
        v-model="intentEntities"
        id="entities"
        name="entities"
        class="jse-theme-dark h-full"
        :stringified="false"
        :mainMenuBar="false"
        :debounce="50"
        style="border-radius: 30px;"
      />

    </div>
    <div class="grid grid-cols-2 gap-4">
      <SingleSelect
        id="scopeId"
        name="scopeId"
        :label="$t('scope')"
        :placeholder="$t('selectScope')"
      :options="availableScopes.map(scope => ({ value: scope.id, label: scope.slug }))"
      required
    />
    <SingleSelect
      id="language"
      name="language"
      :label="$t('language')"
      :placeholder="$t('selectLanguage')"
      :options="getLanguagesAsOptions"
      required
    />
    </div>
    <!-- <div class="flex gap-x-2 [&>*]:grow">
      <InputText
        id="expressions"
        name="expressions"
        :label="$t('firstExpression')"
        required
      />
      <InputText
        id="responses"
        name="responses"
        :label="$t('firstResponse')"
        required
      />
    </div> -->
    <InputSwitch
      id="debug_mode"
      name="debug_mode"
      :label="$t('debugMode')"
      :initial-value="true"
    />
    <div class="text-end">
      <BaseButton type="submit" :disabled="isSubmitting">{{
        $t("startCreate")
      }}</BaseButton>
    </div>
    <BaseBoxColor v-if="error" class="w-full" variant="danger">{{
      error
    }}</BaseBoxColor>
  </FormBuilder>
</template>

<script setup>
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import useIntentStore from "@/stores/intent";
import useScopeStore from "@/stores/scope";
import useModalStore from "@/stores/modal";
import useToastStore from "@/stores/toast";
import validationSchema from "./validationSchemas.js";
import { onMounted } from "vue";
import useAuthStore from "@/stores/auth";
import useOrganizationStore from "@/stores/organization";
import JsonEditorVue from 'json-editor-vue'

const { addIntent } = useIntentStore();
const { fetchScopeList } = useScopeStore();
const { getLanguagesAsOptions } = storeToRefs(useIntentStore());
const { addToast } = useToastStore();
const { closeModal } = useModalStore();
const error = ref(null);
const { t } = useI18n();
const { user } = storeToRefs(useAuthStore());
const intentType = ref(null);
const intentEntities = ref({});
const availableScopes = ref([]);
const { fetchOrganizationList } = useOrganizationStore();
const { scopeList } = storeToRefs(useScopeStore());

const { organizationList } = storeToRefs(useOrganizationStore());
let organizationsLoading = ref(true);
let scopesLoading = ref(true);

onMounted(() => {
  fetchScopeList().then(() => {
    scopesLoading.value = false;
  });
  fetchOrganizationList().then(() => {
    organizationsLoading.value = false;
  });
});


watch([scopeList, organizationList], () => {
  organizationList.value.forEach(org => {
    scopeList.value.forEach(scope => {
      if (scope.OrganizationId === org.id) {
        availableScopes.value.push(scope);
      }
    });
  });
});

/**
 * handle form submit
 * @param {Object} values
 * @param {Function} resetForm
 */
async function onSubmit(values) {
  if (intentType.value === 'dynamic') {
    values.entities = JSON.stringify(intentEntities.value);
  } else {
    values.entities = {};
  }
  try {
    await addIntent(values);
    addToast({
      content: t("itemCreated"),
      variant: "success",
    });
    closeModal();
  } catch (err) {
    error.value = err;
  }
}

function getScopesByOrganization(orgId) {
  fetchScopeList().then(() => {
    availableScopes.value = organizationList.value
      .find(org => org.id === orgId)
      ?.scopes || [];
  });
}

</script>
